import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CalendarPlus,
  KeyRound,
  LogOut,
  Pencil,
  Power,
  Plus,
  RefreshCw,
  Shield,
  Trash2
} from 'lucide-react';
import {
  platformAnnouncementService,
  platformAuthService,
  platformTenantService,
  platformUserService
} from '../../services/platformApi';

const tenantCreateInitialState = {
  companyName: '',
  ownerName: '',
  initialAdminUsername: 'admin',
  initialAdminPassword: '',
  licenseDays: 365
};

const tenantEditInitialState = {
  displayName: '',
  ownerName: '',
  status: 'active',
  licenseStatus: 'active',
  expiresAt: ''
};

const tenantAdminPasswordResetInitialState = {
  username: 'admin',
  password: ''
};

const platformUserInitialState = {
  username: '',
  fullName: '',
  role: 'support',
  password: '',
  isActive: true
};

const announcementInitialState = {
  title: '',
  body: '',
  type: 'featureUpdate',
  targetScope: 'all'
};

const statusLabelMap = {
  active: 'Aktif',
  provisioning: 'Hazırlanıyor',
  suspended: 'Askıda',
  cancelled: 'Kapalı',
  past_due: 'Ödeme Gecikmiş',
  draft: 'Taslak'
};

const licenseStatusLabelMap = {
  active: 'Aktif',
  trial: 'Deneme',
  suspended: 'Askıda',
  expired: 'Süresi Dolmuş',
  cancelled: 'İptal'
};

const announcementTypeLabelMap = {
  featureUpdate: 'Güncelleme',
  billingNotice: 'Lisans Uyarısı',
  maintenance: 'Bakım Duyurusu',
  securityNotice: 'Güvenlik Duyurusu',
  policyChange: 'Politika Değişikliği'
};

const deliveryStatusLabelMap = {
  queued: 'Kuyrukta',
  sent: 'Gönderildi',
  delivered: 'Ulaştı',
  read: 'Okundu',
  failed: 'Hatalı'
};

const deliveryChannelLabelMap = {
  in_app: 'Uygulama içi',
  email: 'E-posta',
  webhook: 'Webhook'
};

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '';
  const padValue = (numericValue) => String(numericValue).padStart(2, '0');
  return [
    `${dateValue.getFullYear()}-${padValue(dateValue.getMonth() + 1)}-${padValue(dateValue.getDate())}`,
    `${padValue(dateValue.getHours())}:${padValue(dateValue.getMinutes())}`
  ].join('T');
};

const formatLicenseExpiryDate = (value) => {
  if (!value) return '-';
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '-';
  return dateValue.toLocaleDateString('tr-TR');
};

const calculateRemainingDays = (expiresAt) => {
  if (!expiresAt) return null;
  const endDate = new Date(expiresAt);
  if (Number.isNaN(endDate.getTime())) return null;
  const nowDate = new Date();
  return Math.ceil((endDate.getTime() - nowDate.getTime()) / (1000 * 60 * 60 * 24));
};

const buildBadgeClassName = (remainingDays) => {
  if (remainingDays == null) return 'bg-gray-100 text-gray-700';
  if (remainingDays < 0) return 'bg-red-100 text-red-700';
  if (remainingDays <= 7) return 'bg-red-100 text-red-700';
  if (remainingDays <= 30) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
};

const extractApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.map((item) => item?.message).filter(Boolean).join(' | ');
  }
  const responseMessage = responseData?.message;
  if (responseMessage && responseMessage !== 'Validation hatası') {
    return responseMessage;
  }
  if (error?.message === 'Network Error') {
    return 'Ağ bağlantı hatası. Lütfen tekrar deneyin.';
  }
  return responseMessage || error?.message || fallbackMessage;
};

const Modal = ({ isOpen, title, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-sm text-gray-600 hover:text-gray-900">
            Kapat
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const PlatformPanel = () => {
  const navigate = useNavigate();
  const platformUser = useMemo(() => platformAuthService.getUser(), []);

  const [tenants, setTenants] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  const [isTenantsLoading, setIsTenantsLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isAnnouncementsLoading, setIsAnnouncementsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [tenantSearchText, setTenantSearchText] = useState('');
  const [tenantStatusFilter, setTenantStatusFilter] = useState('all');

  const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false);
  const [isTenantEditModalOpen, setIsTenantEditModalOpen] = useState(false);
  const [isTenantRenewModalOpen, setIsTenantRenewModalOpen] = useState(false);
  const [isTenantAdminPasswordModalOpen, setIsTenantAdminPasswordModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isUserPasswordModalOpen, setIsUserPasswordModalOpen] = useState(false);

  const [tenantCreateForm, setTenantCreateForm] = useState(tenantCreateInitialState);
  const [tenantEditForm, setTenantEditForm] = useState(tenantEditInitialState);
  const [tenantRenewDays, setTenantRenewDays] = useState(365);
  const [tenantRenewStrategy, setTenantRenewStrategy] = useState('add');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantAdminPasswordResetForm, setTenantAdminPasswordResetForm] = useState(tenantAdminPasswordResetInitialState);
  const [tenantAdminOptions, setTenantAdminOptions] = useState([]);

  const [announcementForm, setAnnouncementForm] = useState(announcementInitialState);
  const [announcementTenantIds, setAnnouncementTenantIds] = useState([]);
  const [expandedAnnouncementIds, setExpandedAnnouncementIds] = useState([]);
  const [deliveryFilterMap, setDeliveryFilterMap] = useState({});
  const [deliveryMap, setDeliveryMap] = useState({});

  const [platformUserForm, setPlatformUserForm] = useState(platformUserInitialState);
  const [selectedPlatformUser, setSelectedPlatformUser] = useState(null);
  const [newPlatformPassword, setNewPlatformPassword] = useState('');
  const [passwordModalState, setPasswordModalState] = useState({
    isOpen: false,
    title: '',
    username: '',
    password: ''
  });

  const openPasswordModal = ({ title, username, password }) => {
    setPasswordModalState({
      isOpen: true,
      title: title || 'Yeni Şifre',
      username: username || '',
      password: password || ''
    });
  };

  const closePasswordModal = () => {
    setPasswordModalState({
      isOpen: false,
      title: '',
      username: '',
      password: ''
    });
  };

  const copyPasswordToClipboard = async () => {
    const textValue = String(passwordModalState.password || '');
    if (!textValue) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textValue);
      } else {
        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = textValue;
        document.body.appendChild(tempTextArea);
        tempTextArea.select();
        document.execCommand('copy');
        document.body.removeChild(tempTextArea);
      }
      toast.success('Şifre kopyalandı');
    } catch (_error) {
      toast.error('Şifre kopyalanamadı');
    }
  };

  const loadTenants = useCallback(async () => {
    setIsTenantsLoading(true);
    try {
      const response = await platformTenantService.list();
      if (!response?.success) throw new Error(response?.message || 'Kayıtlar yüklenemedi');
      setTenants(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Kullanıcı listesi yüklenemedi');
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/platform/login', { replace: true });
      }
    } finally {
      setIsTenantsLoading(false);
    }
  }, [navigate]);

  const loadPlatformUsers = useCallback(async () => {
    if (platformUser?.role !== 'superuser') return;
    setIsUsersLoading(true);
    try {
      const response = await platformUserService.list();
      if (!response?.success) throw new Error(response?.message || 'Platform kullanıcıları yüklenemedi');
      setPlatformUsers(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Platform kullanıcıları yüklenemedi');
    } finally {
      setIsUsersLoading(false);
    }
  }, [platformUser?.role]);

  const loadAnnouncements = useCallback(async () => {
    setIsAnnouncementsLoading(true);
    try {
      const response = await platformAnnouncementService.list(25);
      if (!response?.success) throw new Error(response?.message || 'Duyurular yüklenemedi');
      setAnnouncements(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Duyurular yüklenemedi');
    } finally {
      setIsAnnouncementsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
    loadPlatformUsers();
    loadAnnouncements();
  }, [loadAnnouncements, loadPlatformUsers, loadTenants]);

  const filteredTenants = useMemo(() => {
    const queryText = tenantSearchText.trim().toLowerCase();
    return tenants.filter((tenantItem) => {
      const matchesSearch = !queryText
        || String(tenantItem.displayName || '').toLowerCase().includes(queryText)
        || String(tenantItem.code || '').toLowerCase().includes(queryText)
        || String(tenantItem.metadata?.ownerName || '').toLowerCase().includes(queryText);
      const matchesStatus = tenantStatusFilter === 'all' || tenantItem.status === tenantStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [tenantSearchText, tenantStatusFilter, tenants]);

  const createTenantCode = (companyName) => {
    return String(companyName || '')
      .trim()
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const resetTenantCreateModal = () => {
    setTenantCreateForm(tenantCreateInitialState);
    setIsCreateTenantModalOpen(false);
  };

  const handleCreateTenant = async (event) => {
    event.preventDefault();
    if (isSaving) return;
    const companyCode = createTenantCode(tenantCreateForm.companyName);
    if (!companyCode) {
      toast.error('Firma adı geçerli bir kod üretmiyor');
      return;
    }
    if (tenantCreateForm.initialAdminPassword && tenantCreateForm.initialAdminPassword.length < 8) {
      toast.error('İlk kullanıcı şifresi en az 8 karakter olmalı veya boş bırakılmalı.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        tenant: {
          code: companyCode,
          displayName: tenantCreateForm.companyName.trim(),
          ownerName: tenantCreateForm.ownerName.trim() || null,
          subdomain: companyCode,
          dbHost: '127.0.0.1',
          dbPort: 5432,
          dbName: companyCode,
          dbUser: companyCode,
          initialAdminUsername: tenantCreateForm.initialAdminUsername.trim() || 'admin',
          initialAdminPassword: tenantCreateForm.initialAdminPassword || null,
          timezone: 'Europe/Istanbul',
          gracePeriodDays: 7,
          autoProvision: true
        },
        license: {
          status: 'active',
          planCode: 'standard',
          durationDays: Number(tenantCreateForm.licenseDays || 365)
        }
      };

      const response = await platformTenantService.create(payload);
      if (!response?.success) throw new Error(response?.message || 'Kullanıcı oluşturulamadı');
      const provisioning = response?.data?.provisioning || {};
      const dbCredentials = provisioning?.dbCredentials;
      const adminCredentials = provisioning?.credentials;

      if (provisioning.status === 'failed') {
        toast.error(provisioning.errorMessage || 'Firma kaydı açıldı ama veritabanı hazırlanamadı');
        resetTenantCreateModal();
        await loadTenants();
        return;
      }

      toast.success('Yeni kullanıcı kaydı açıldı ve altyapı hazırlandı');
      if (adminCredentials?.username) {
        toast.success(`İlk admin: ${adminCredentials.username}`);
      }
      if (dbCredentials?.dbPassword) {
        toast.success(`DB şifresi üretildi: ${dbCredentials.dbPassword}`);
      }
      if (adminCredentials?.password) {
        openPasswordModal({
          title: 'İlk Admin Şifresi',
          username: adminCredentials.username || tenantCreateForm.initialAdminUsername || 'admin',
          password: adminCredentials.password
        });
      }
      resetTenantCreateModal();
      await loadTenants();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Kullanıcı oluşturulamadı'));
    } finally {
      setIsSaving(false);
    }
  };

  const openTenantEditModal = (tenantItem) => {
    setSelectedTenant(tenantItem);
    setTenantEditForm({
      displayName: tenantItem.displayName || '',
      ownerName: tenantItem.metadata?.ownerName || '',
      status: tenantItem.status || 'active',
      licenseStatus: tenantItem.license?.status || 'active',
      expiresAt: formatDateTimeLocal(tenantItem.license?.expiresAt)
    });
    setTenantRenewDays(365);
    setTenantRenewStrategy('add');
    setIsTenantEditModalOpen(true);
  };

  const handleUpdateTenant = async (event) => {
    event.preventDefault();
    if (!selectedTenant) return;

    setIsSaving(true);
    try {
      const tenantPayload = {
        displayName: tenantEditForm.displayName.trim(),
        ownerName: tenantEditForm.ownerName.trim(),
        status: tenantEditForm.status
      };
      const licensePayload = {
        status: tenantEditForm.licenseStatus,
        tenantStatus: tenantEditForm.status,
        expiresAt: tenantEditForm.expiresAt ? new Date(tenantEditForm.expiresAt).toISOString() : undefined
      };

      await platformTenantService.update(selectedTenant.id, tenantPayload);
      await platformTenantService.updateLicense(selectedTenant.id, licensePayload);
      toast.success('Kullanıcı bilgileri güncellendi');
      setIsTenantEditModalOpen(false);
      setSelectedTenant(null);
      await loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Güncelleme başarısız');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRenewLicense = async () => {
    if (!selectedTenant) return;
    setIsSaving(true);
    try {
      await platformTenantService.renewLicense(selectedTenant.id, {
        days: Number(tenantRenewDays || 1),
        strategy: tenantRenewStrategy,
        tenantStatus: tenantEditForm.status === 'cancelled' ? 'active' : tenantEditForm.status
      });
      toast.success('Lisans süresi güncellendi');
      await loadTenants();
      setIsTenantRenewModalOpen(false);
      setSelectedTenant(null);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Lisans yenilenemedi');
    } finally {
      setIsSaving(false);
    }
  };

  const updateTenantStatusQuickly = async (tenantId, nextStatus) => {
    try {
      await platformTenantService.updateLicense(tenantId, { tenantStatus: nextStatus });
      toast.success('Durum güncellendi');
      await loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Durum güncellenemedi');
    }
  };

  const openTenantAdminPasswordResetModal = async (tenantItem) => {
    const fallbackUsername = tenantItem.currentAdminUsername || tenantItem.metadata?.initialAdminUsername || 'admin';
    setSelectedTenant(tenantItem);
    setTenantAdminOptions(fallbackUsername ? [{ id: null, username: fallbackUsername, role: 'admin' }] : []);
    setTenantAdminPasswordResetForm({
      username: fallbackUsername,
      password: ''
    });
    setIsTenantAdminPasswordModalOpen(true);

    try {
      const response = await platformTenantService.getPrimaryAdmin(tenantItem.id);
      const liveAdmins = Array.isArray(response?.data?.admins) && response.data.admins.length > 0
        ? response.data.admins
        : (response?.data?.admin ? [response.data.admin] : []);
      if (liveAdmins.length > 0) {
        setTenantAdminOptions(liveAdmins);
        setTenantAdminPasswordResetForm((prev) => ({
          ...prev,
          username: liveAdmins[0].username
        }));
      }
    } catch (_error) {
      // Metadata/fallback kullanıcı adı kalsın
    }
  };

  const handleResetTenantAdminPassword = async (event) => {
    event.preventDefault();
    if (!selectedTenant) return;
    if (tenantAdminPasswordResetForm.password && tenantAdminPasswordResetForm.password.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı veya boş bırakılmalı.');
      return;
    }

    try {
      const response = await platformTenantService.resetAdminPassword(selectedTenant.id, {
        username: tenantAdminPasswordResetForm.username?.trim() || undefined,
        password: tenantAdminPasswordResetForm.password || undefined
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Admin şifresi yenilenemedi');
      }
      const credentials = response?.data?.credentials || {};
      const resolvedUsername = credentials.username || tenantAdminPasswordResetForm.username || 'admin';
      if (credentials.resolvedFromFallback && credentials.username) {
        toast.success(`Eski kullanıcı bulunamadı; güncel admin ile devam edildi: ${credentials.username}`);
      }
      if (credentials.passwordGenerated && credentials.password) {
        openPasswordModal({
          title: `${selectedTenant.displayName} Admin Şifresi`,
          username: resolvedUsername,
          password: credentials.password
        });
      } else {
        toast.success(`Admin şifresi güncellendi (${resolvedUsername})`);
      }
      setIsTenantAdminPasswordModalOpen(false);
      setSelectedTenant(null);
      setTenantAdminOptions([]);
      setTenantAdminPasswordResetForm(tenantAdminPasswordResetInitialState);
      await loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Admin şifresi yenilenemedi');
    }
  };

  const handleToggleTenantStatus = async (tenantItem) => {
    const nextStatus = tenantItem.status === 'active' ? 'suspended' : 'active';
    await updateTenantStatusQuickly(tenantItem.id, nextStatus);
  };

  const openTenantRenewModal = (tenantItem) => {
    setSelectedTenant(tenantItem);
    setTenantRenewDays(365);
    setTenantRenewStrategy('add');
    setIsTenantRenewModalOpen(true);
  };

  const handleDeleteTenantWithDatabase = async (tenantItem) => {
    const confirmed = window.confirm(
      `${tenantItem.displayName} firması ve ${tenantItem.dbName} veritabanı tamamen silinecek. Devam etmek istiyor musunuz?`
    );
    if (!confirmed) return;

    try {
      const response = await platformTenantService.remove(tenantItem.id);
      if (!response?.success) {
        throw new Error(response?.message || 'Silme işlemi başarısız');
      }
      toast.success(`${tenantItem.displayName} tamamen silindi`);
      await loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Silme işlemi başarısız');
    }
  };

  const handleCreateAnnouncement = async (event) => {
    event.preventDefault();
    if (announcementForm.targetScope === 'single' && announcementTenantIds.length !== 1) {
      toast.error('Tekli seçimde 1 kullanıcı seçmelisiniz');
      return;
    }
    if (announcementForm.targetScope === 'group' && announcementTenantIds.length === 0) {
      toast.error('Grup seçiminde en az 1 kullanıcı seçmelisiniz');
      return;
    }

    try {
      const response = await platformAnnouncementService.create({
        title: announcementForm.title.trim(),
        body: announcementForm.body.trim(),
        type: announcementForm.type,
        targetScope: announcementForm.targetScope,
        channels: ['in_app'],
        tenantIds: announcementForm.targetScope === 'all' ? [] : announcementTenantIds
      });
      if (!response?.success) throw new Error(response?.message || 'Duyuru gönderilemedi');
      const deliveredCount = Number(response.data?.deliverySummary?.deliveredCount || 0);
      const failedCount = Number(response.data?.deliverySummary?.failedCount || 0);
      if (failedCount > 0 && deliveredCount === 0) {
        toast.error(`Duyuru gönderilemedi (${failedCount} hatalı teslimat)`);
      } else if (failedCount > 0) {
        toast.success(`Duyuru gönderildi (${deliveredCount} ulaştı, ${failedCount} hatalı)`);
      } else {
        toast.success(`Duyuru gönderildi (${response.data?.deliveryCount || deliveredCount || 0})`);
      }
      setAnnouncementForm(announcementInitialState);
      setAnnouncementTenantIds([]);
      await loadAnnouncements();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Duyuru gönderilemedi'));
    }
  };

  const toggleAnnouncementTenant = (tenantId) => {
    setAnnouncementTenantIds((previous) => {
      if (previous.includes(tenantId)) {
        return previous.filter((itemId) => itemId !== tenantId);
      }
      return [...previous, tenantId];
    });
  };

  const loadDeliveries = async (announcementId, statusFilter = 'all') => {
    const response = await platformAnnouncementService.listDeliveries(announcementId, { status: statusFilter, limit: 50, offset: 0 });
    if (!response?.success) throw new Error(response?.message || 'Teslimat kayıtları yüklenemedi');
    setDeliveryMap((previous) => ({ ...previous, [announcementId]: response.data || [] }));
  };

  const toggleAnnouncementDetails = async (announcementId) => {
    const isExpanded = expandedAnnouncementIds.includes(announcementId);
    if (isExpanded) {
      setExpandedAnnouncementIds((previous) => previous.filter((idValue) => idValue !== announcementId));
      return;
    }
    setExpandedAnnouncementIds((previous) => [...previous, announcementId]);
    if (!deliveryMap[announcementId]) {
      await loadDeliveries(announcementId, deliveryFilterMap[announcementId] || 'all');
    }
  };

  const handleRetryDelivery = async (announcementId, deliveryId) => {
    try {
      const response = await platformAnnouncementService.retryDelivery(announcementId, deliveryId);
      if (!response?.success) throw new Error(response?.message || 'Kayıt tekrar kuyruğa alınamadı');
      toast.success('Kayıt tekrar kuyruğa alındı');
      await loadDeliveries(announcementId, deliveryFilterMap[announcementId] || 'all');
      await loadAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Tekrar deneme başarısız');
    }
  };

  const openCreatePlatformUserModal = () => {
    setSelectedPlatformUser(null);
    setPlatformUserForm(platformUserInitialState);
    setIsUserModalOpen(true);
  };

  const openEditPlatformUserModal = (userItem) => {
    setSelectedPlatformUser(userItem);
    setPlatformUserForm({
      username: userItem.username || '',
      fullName: userItem.metadata?.fullName || '',
      role: userItem.role || 'support',
      password: '',
      isActive: Boolean(userItem.isActive)
    });
    setIsUserModalOpen(true);
  };

  const handleSavePlatformUser = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      if (selectedPlatformUser) {
        const response = await platformUserService.update(selectedPlatformUser.id, {
          username: platformUserForm.username,
          fullName: platformUserForm.fullName,
          role: platformUserForm.role,
          isActive: platformUserForm.isActive
        });
        if (!response?.success) throw new Error(response?.message || 'Kullanıcı güncellenemedi');
        toast.success('Platform kullanıcısı güncellendi');
      } else {
        const response = await platformUserService.create({
          username: platformUserForm.username,
          fullName: platformUserForm.fullName,
          role: platformUserForm.role,
          password: platformUserForm.password || undefined,
          isActive: platformUserForm.isActive
        });
        if (!response?.success) throw new Error(response?.message || 'Kullanıcı oluşturulamadı');
        toast.success('Platform kullanıcısı oluşturuldu');
        if (response?.data?.generatedPassword) {
          openPasswordModal({
            title: 'Platform Kullanıcısı Şifresi',
            username: response?.data?.user?.username || platformUserForm.username,
            password: response.data.generatedPassword
          });
        }
      }
      setIsUserModalOpen(false);
      await loadPlatformUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Kayıt işlemi başarısız');
    } finally {
      setIsSaving(false);
    }
  };

  const openPlatformUserPasswordModal = (userItem) => {
    setSelectedPlatformUser(userItem);
    setNewPlatformPassword('');
    setIsUserPasswordModalOpen(true);
  };

  const handleResetPlatformUserPassword = async (event) => {
    event.preventDefault();
    if (!selectedPlatformUser) return;
    setIsSaving(true);
    try {
      const response = await platformUserService.resetPassword(selectedPlatformUser.id, {
        password: newPlatformPassword || undefined
      });
      if (!response?.success) throw new Error(response?.message || 'Şifre güncellenemedi');
      toast.success('Şifre güncellendi');
      if (response?.data?.generatedPassword) {
        openPasswordModal({
          title: 'Platform Kullanıcısı Yeni Şifresi',
          username: selectedPlatformUser?.username || '',
          password: response.data.generatedPassword
        });
      }
      setIsUserPasswordModalOpen(false);
      await loadPlatformUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Şifre güncellemesi başarısız');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePlatformUser = async (userItem) => {
    if (!userItem?.id) return;
    if (Number(userItem.id) === Number(platformUser?.id)) {
      toast.error('Oturum açtığınız kullanıcıyı silemezsiniz');
      return;
    }

    const confirmed = window.confirm(
      `${userItem.username} kullanıcısı silinecek. Devam etmek istiyor musunuz?`
    );
    if (!confirmed) return;

    try {
      const response = await platformUserService.remove(userItem.id);
      if (!response?.success) throw new Error(response?.message || 'Silme işlemi başarısız');
      toast.success(`${userItem.username} silindi`);
      await loadPlatformUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Silme işlemi başarısız');
    }
  };

  const handleLogout = () => {
    platformAuthService.logout();
    navigate('/platform/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow p-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Platform Yönetim Paneli</h1>
            <p className="text-sm text-gray-600">
              Giriş yapan: {platformUser?.username || '-'} ({platformUser?.role || '-'})
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsCreateTenantModalOpen(true)}
              title="Yeni Kullanıcı Oluştur"
              aria-label="Yeni Kullanıcı Oluştur"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={18} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              title="Çıkış Yap"
              aria-label="Çıkış Yap"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-gray-700 hover:bg-gray-800 text-white"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Kullanıcı Listesi</h2>
            <button
              type="button"
              onClick={loadTenants}
              title="Yenile"
              aria-label="Yenile"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-700 hover:bg-gray-100"
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <input
              value={tenantSearchText}
              onChange={(event) => setTenantSearchText(event.target.value)}
              placeholder="Firma / kod / firma sahibi ara"
              className="px-3 py-2 rounded-md border text-sm"
            />
            <select
              value={tenantStatusFilter}
              onChange={(event) => setTenantStatusFilter(event.target.value)}
              className="px-3 py-2 rounded-md border text-sm"
            >
              <option value="all">Tüm durumlar</option>
              {Object.keys(statusLabelMap).map((statusKey) => (
                <option key={statusKey} value={statusKey}>{statusLabelMap[statusKey]}</option>
              ))}
            </select>
          </div>
          {isTenantsLoading ? (
            <p className="text-sm text-gray-600">Yükleniyor...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-2">Firma</th>
                    <th className="py-2 pr-2">Sahip</th>
                    <th className="py-2 pr-2">Durum</th>
                    <th className="py-2 pr-2">Lisans</th>
                    <th className="py-2 pr-2">Kalan Gün</th>
                    <th className="py-2 pr-2">Lisans son erme tarihi</th>
                    <th className="py-2">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenantItem) => {
                    const remainingDays = calculateRemainingDays(tenantItem.license?.expiresAt);
                    const isActiveTenant = tenantItem.status === 'active';
                    return (
                      <tr key={tenantItem.id} className="border-b">
                        <td className="py-2 pr-2">
                          <div className="font-medium text-gray-900">{tenantItem.displayName}</div>
                          <div className="text-xs text-gray-500">{tenantItem.code}</div>
                        </td>
                        <td className="py-2 pr-2">{tenantItem.metadata?.ownerName || '-'}</td>
                        <td className="py-2 pr-2">{statusLabelMap[tenantItem.status] || tenantItem.status}</td>
                        <td className="py-2 pr-2">
                          {licenseStatusLabelMap[tenantItem.license?.status] || tenantItem.license?.status || '-'}
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs ${buildBadgeClassName(remainingDays)}`}>
                            {remainingDays == null ? '-' : `${remainingDays} gün`}
                          </span>
                        </td>
                        <td className="py-2 pr-2">
                          {formatLicenseExpiryDate(tenantItem.license?.expiresAt)}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              onClick={() => openTenantEditModal(tenantItem)}
                              title="Düzenle"
                              aria-label="Düzenle"
                              className="inline-flex h-7 w-7 items-center justify-center rounded border text-blue-600 hover:bg-blue-50"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openTenantRenewModal(tenantItem)}
                              title="Lisans Yenile"
                              aria-label="Lisans Yenile"
                              className="inline-flex h-7 w-7 items-center justify-center rounded border text-emerald-600 hover:bg-emerald-50"
                            >
                              <CalendarPlus size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openTenantAdminPasswordResetModal(tenantItem)}
                              title="Admin Şifresi Yenile"
                              aria-label="Admin Şifresi Yenile"
                              className="inline-flex h-7 w-7 items-center justify-center rounded border text-amber-500 hover:bg-amber-50"
                            >
                              <Shield size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleTenantStatus(tenantItem)}
                              title={isActiveTenant ? 'Pasife Al' : 'Aktif Et'}
                              aria-label={isActiveTenant ? 'Pasife Al' : 'Aktif Et'}
                              className={`inline-flex h-7 w-7 items-center justify-center rounded border ${
                                isActiveTenant
                                  ? 'text-emerald-600 hover:bg-emerald-50'
                                  : 'text-red-600 hover:bg-red-50'
                              }`}
                            >
                              <Power size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTenantWithDatabase(tenantItem)}
                              title="Firmayı Sil (DB Dahil)"
                              aria-label="Firmayı Sil (DB Dahil)"
                              className="inline-flex h-7 w-7 items-center justify-center rounded border text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredTenants.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-4 text-sm text-gray-500">Kayıt bulunamadı.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <form onSubmit={handleCreateAnnouncement} className="bg-white rounded-xl shadow p-5 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Duyuru Paneli</h2>
            <input
              name="title"
              value={announcementForm.title}
              onChange={(event) => setAnnouncementForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Başlık (en az 3 karakter)"
              required
              minLength={3}
              className="w-full px-3 py-2 rounded-md border text-sm"
            />
            <textarea
              name="body"
              value={announcementForm.body}
              onChange={(event) => setAnnouncementForm((prev) => ({ ...prev, body: event.target.value }))}
              placeholder="Duyuru metni (en az 3 karakter)"
              rows={4}
              required
              minLength={3}
              className="w-full px-3 py-2 rounded-md border text-sm"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <select
                value={announcementForm.type}
                onChange={(event) => setAnnouncementForm((prev) => ({ ...prev, type: event.target.value }))}
                className="px-3 py-2 rounded-md border text-sm"
              >
                {Object.keys(announcementTypeLabelMap).map((typeKey) => (
                  <option key={typeKey} value={typeKey}>{announcementTypeLabelMap[typeKey]}</option>
                ))}
              </select>
              <select
                value={announcementForm.targetScope}
                onChange={(event) => setAnnouncementForm((prev) => ({ ...prev, targetScope: event.target.value }))}
                className="px-3 py-2 rounded-md border text-sm"
              >
                <option value="all">Tüm Kullanıcılar</option>
                <option value="group">Seçili Grup</option>
                <option value="single">Tek Kullanıcı</option>
              </select>
            </div>
            {announcementForm.targetScope !== 'all' && (
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                {tenants.map((tenantItem) => (
                  <label key={`announcement-tenant-${tenantItem.id}`} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={announcementTenantIds.includes(tenantItem.id)}
                      onChange={() => toggleAnnouncementTenant(tenantItem.id)}
                    />
                    <span>{tenantItem.displayName}</span>
                  </label>
                ))}
              </div>
            )}
            <button type="submit" className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
              Duyuru Gönder
            </button>
          </form>

          <div className="bg-white rounded-xl shadow p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Duyuru Geçmişi</h2>
              <button
                type="button"
                onClick={loadAnnouncements}
                title="Yenile"
                aria-label="Yenile"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-700 hover:bg-gray-100"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            {isAnnouncementsLoading ? (
              <p className="text-sm text-gray-600">Yükleniyor...</p>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcementItem) => (
                  <div key={announcementItem.id} className="border rounded-md p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium text-gray-900">{announcementItem.title}</div>
                        <div className="text-xs text-gray-500">
                          {announcementTypeLabelMap[announcementItem.type] || announcementItem.type} -{' '}
                          {announcementItem.createdAt ? new Date(announcementItem.createdAt).toLocaleString('tr-TR') : '-'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleAnnouncementDetails(announcementItem.id)}
                        className="px-2 py-1 rounded border text-xs"
                      >
                        {expandedAnnouncementIds.includes(announcementItem.id) ? 'Detayı Gizle' : 'Teslimat Detayı'}
                      </button>
                    </div>
                    {expandedAnnouncementIds.includes(announcementItem.id) && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <select
                            value={deliveryFilterMap[announcementItem.id] || 'all'}
                            onChange={async (event) => {
                              const nextStatus = event.target.value;
                              setDeliveryFilterMap((prev) => ({ ...prev, [announcementItem.id]: nextStatus }));
                              await loadDeliveries(announcementItem.id, nextStatus);
                            }}
                            className="px-2 py-1 rounded border text-xs"
                          >
                            <option value="all">Tüm Durumlar</option>
                            <option value="queued">Kuyrukta</option>
                            <option value="sent">Gönderildi</option>
                            <option value="delivered">Ulaştı</option>
                            <option value="read">Okundu</option>
                            <option value="failed">Hatalı</option>
                          </select>
                          <span className="text-xs text-gray-500">
                            Toplam: {(deliveryMap[announcementItem.id] || []).length}
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left border-b">
                                <th className="py-1 pr-2">Firma</th>
                                <th className="py-1 pr-2">Kanal</th>
                                <th className="py-1 pr-2">Durum</th>
                                <th className="py-1 pr-2">Hata</th>
                                <th className="py-1">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(deliveryMap[announcementItem.id] || []).map((deliveryItem) => {
                                const tenantLabel = tenants.find((tenantItem) => Number(tenantItem.id) === Number(deliveryItem.tenantId))?.displayName
                                  || `#${deliveryItem.tenantId}`;
                                return (
                                  <tr key={deliveryItem.id} className="border-b">
                                    <td className="py-1 pr-2">{tenantLabel}</td>
                                    <td className="py-1 pr-2">{deliveryChannelLabelMap[deliveryItem.channel] || deliveryItem.channel}</td>
                                    <td className="py-1 pr-2">{deliveryStatusLabelMap[deliveryItem.status] || deliveryItem.status}</td>
                                    <td className="py-1 pr-2">{deliveryItem.errorMessage || '-'}</td>
                                    <td className="py-1">
                                      {deliveryItem.status === 'failed' ? (
                                        <button
                                          type="button"
                                          onClick={() => handleRetryDelivery(announcementItem.id, deliveryItem.id)}
                                          className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white"
                                        >
                                          Tekrar Dene
                                        </button>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {announcements.length === 0 && <p className="text-sm text-gray-500">Duyuru kaydı bulunamadı.</p>}
              </div>
            )}
          </div>
        </div>

        {platformUser?.role === 'superuser' && (
          <div className="bg-white rounded-xl shadow p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Platform Kullanıcıları</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadPlatformUsers}
                  title="Yenile"
                  aria-label="Yenile"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-700 hover:bg-gray-100"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  type="button"
                  onClick={openCreatePlatformUserModal}
                  title="Kullanıcı Ekle"
                  aria-label="Kullanıcı Ekle"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            {isUsersLoading ? (
              <p className="text-sm text-gray-600">Yükleniyor...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-2">Kullanıcı Adı</th>
                      <th className="py-2 pr-2">Ad Soyad</th>
                      <th className="py-2 pr-2">Rol</th>
                      <th className="py-2 pr-2">Durum</th>
                      <th className="py-2">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformUsers.map((userItem) => {
                      const isCurrentUser = Number(userItem.id) === Number(platformUser?.id);
                      return (
                        <tr key={userItem.id} className="border-b">
                          <td className="py-2 pr-2">{userItem.username}</td>
                          <td className="py-2 pr-2">{userItem.metadata?.fullName || '-'}</td>
                          <td className="py-2 pr-2">{userItem.role}</td>
                          <td className="py-2 pr-2">{userItem.isActive ? 'Aktif' : 'Pasif'}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              <button
                                type="button"
                                onClick={() => openEditPlatformUserModal(userItem)}
                                title="Düzenle"
                                aria-label="Düzenle"
                                className="inline-flex h-7 w-7 items-center justify-center rounded border text-blue-600 hover:bg-blue-50"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openPlatformUserPasswordModal(userItem)}
                                title="Şifre Değiştir"
                                aria-label="Şifre Değiştir"
                                className="inline-flex h-7 w-7 items-center justify-center rounded border text-amber-500 hover:bg-amber-50"
                              >
                                <KeyRound size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePlatformUser(userItem)}
                                disabled={isCurrentUser}
                                title={isCurrentUser ? 'Kendi hesabınızı silemezsiniz' : 'Sil'}
                                aria-label={isCurrentUser ? 'Kendi hesabınızı silemezsiniz' : 'Sil'}
                                className={`inline-flex h-7 w-7 items-center justify-center rounded border ${
                                  isCurrentUser
                                    ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                    : 'text-red-600 hover:bg-red-50'
                                }`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {platformUsers.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-sm text-gray-500">Kayıt bulunamadı.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={isCreateTenantModalOpen} title="Yeni Kullanıcı Oluştur" onClose={resetTenantCreateModal}>
        <form onSubmit={handleCreateTenant} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Firma Adı</label>
              <input
                value={tenantCreateForm.companyName}
                onChange={(event) => setTenantCreateForm((prev) => ({ ...prev, companyName: event.target.value }))}
                required
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Firma Sahibi</label>
              <input
                value={tenantCreateForm.ownerName}
                onChange={(event) => setTenantCreateForm((prev) => ({ ...prev, ownerName: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">İlk Kullanıcı Adı</label>
              <input
                value={tenantCreateForm.initialAdminUsername}
                onChange={(event) => setTenantCreateForm((prev) => ({ ...prev, initialAdminUsername: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">İlk Kullanıcı Şifresi</label>
              <input
                type="password"
                value={tenantCreateForm.initialAdminPassword}
                onChange={(event) => setTenantCreateForm((prev) => ({ ...prev, initialAdminPassword: event.target.value }))}
                placeholder="Boş bırakırsan otomatik üretilir"
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lisans Günü</label>
              <input
                type="number"
                min={1}
                value={tenantCreateForm.licenseDays}
                onChange={(event) => setTenantCreateForm((prev) => ({ ...prev, licenseDays: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Otomatik Alanlar</label>
              <div className="px-3 py-2 rounded-md border bg-gray-50 text-xs text-gray-600">
                Subdomain = firma kodu, DB Name/User = firma kodu, DB şifresi otomatik üretilir.
              </div>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm"
          >
            {isSaving ? 'Kaydediliyor...' : 'Kullanıcı Oluştur'}
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={isTenantEditModalOpen}
        title={selectedTenant ? `${selectedTenant.displayName} Düzenle` : 'Kayıt Düzenle'}
        onClose={() => {
          setIsTenantEditModalOpen(false);
          setSelectedTenant(null);
        }}
      >
        <form onSubmit={handleUpdateTenant} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Firma Adı</label>
              <input
                value={tenantEditForm.displayName}
                onChange={(event) => setTenantEditForm((prev) => ({ ...prev, displayName: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Firma Sahibi</label>
              <input
                value={tenantEditForm.ownerName}
                onChange={(event) => setTenantEditForm((prev) => ({ ...prev, ownerName: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Firma Durumu</label>
              <select
                value={tenantEditForm.status}
                onChange={(event) => setTenantEditForm((prev) => ({ ...prev, status: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              >
                {Object.keys(statusLabelMap).map((statusKey) => (
                  <option key={statusKey} value={statusKey}>{statusLabelMap[statusKey]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lisans Durumu</label>
              <select
                value={tenantEditForm.licenseStatus}
                onChange={(event) => setTenantEditForm((prev) => ({ ...prev, licenseStatus: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              >
                {Object.keys(licenseStatusLabelMap).map((statusKey) => (
                  <option key={statusKey} value={statusKey}>{licenseStatusLabelMap[statusKey]}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Lisans Bitiş Tarihi</label>
              <input
                type="datetime-local"
                value={tenantEditForm.expiresAt}
                onChange={(event) => setTenantEditForm((prev) => ({ ...prev, expiresAt: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={isSaving} className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
              Kaydet
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isTenantRenewModalOpen}
        title={selectedTenant ? `${selectedTenant.displayName} Lisans Yenile` : 'Lisans Yenile'}
        onClose={() => {
          setIsTenantRenewModalOpen(false);
          setSelectedTenant(null);
        }}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Lisans süresini ekleme veya sıfırdan başlatma seçeneklerinden birini seçin.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="number"
              min={1}
              value={tenantRenewDays}
              onChange={(event) => setTenantRenewDays(event.target.value)}
              className="px-3 py-2 rounded-md border text-sm"
            />
            <select
              value={tenantRenewStrategy}
              onChange={(event) => setTenantRenewStrategy(event.target.value)}
              className="px-3 py-2 rounded-md border text-sm"
            >
              <option value="add">Kalan günün üstüne ekle</option>
              <option value="override">Bugünden itibaren yeniden başlat</option>
            </select>
            <button
              type="button"
              onClick={handleRenewLicense}
              disabled={isSaving}
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
            >
              Lisansı Yenile
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isTenantAdminPasswordModalOpen}
        title={selectedTenant ? `${selectedTenant.displayName} Admin Şifre Reset` : 'Admin Şifre Reset'}
        onClose={() => {
          setIsTenantAdminPasswordModalOpen(false);
          setTenantAdminOptions([]);
          setTenantAdminPasswordResetForm(tenantAdminPasswordResetInitialState);
        }}
      >
        <form onSubmit={handleResetTenantAdminPassword} className="space-y-3">
          <p className="text-sm text-gray-600">
            Şifreyi boş bırakırsan sistem otomatik güçlü bir şifre üretir.
            {tenantAdminOptions.length > 1 ? ' Birden fazla admin varsa hangisinin şifresini sıfırlayacağını seç.' : ''}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Kullanıcı Adı</label>
              {tenantAdminOptions.length > 1 ? (
                <select
                  value={tenantAdminPasswordResetForm.username}
                  onChange={(event) => setTenantAdminPasswordResetForm((prev) => ({ ...prev, username: event.target.value }))}
                  className="w-full px-3 py-2 rounded-md border text-sm bg-white"
                >
                  {tenantAdminOptions.map((adminOption) => (
                    <option key={`${adminOption.id || 'x'}-${adminOption.username}`} value={adminOption.username}>
                      {adminOption.username} ({adminOption.role || 'admin'})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={tenantAdminPasswordResetForm.username}
                  onChange={(event) => setTenantAdminPasswordResetForm((prev) => ({ ...prev, username: event.target.value }))}
                  className="w-full px-3 py-2 rounded-md border text-sm"
                />
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Yeni Şifre (Opsiyonel)</label>
              <input
                type="password"
                value={tenantAdminPasswordResetForm.password}
                onChange={(event) => setTenantAdminPasswordResetForm((prev) => ({ ...prev, password: event.target.value }))}
                placeholder="Boş bırak: otomatik üret"
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
          </div>
          <button type="submit" disabled={isSaving} className="px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm">
            Şifreyi Resetle
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={isUserModalOpen}
        title={selectedPlatformUser ? 'Platform Kullanıcısı Düzenle' : 'Platform Kullanıcısı Ekle'}
        onClose={() => setIsUserModalOpen(false)}
      >
        <form onSubmit={handleSavePlatformUser} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Kullanıcı Adı</label>
              <input
                value={platformUserForm.username}
                onChange={(event) => setPlatformUserForm((prev) => ({ ...prev, username: event.target.value }))}
                required
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Ad Soyad</label>
              <input
                value={platformUserForm.fullName}
                onChange={(event) => setPlatformUserForm((prev) => ({ ...prev, fullName: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Rol</label>
              <select
                value={platformUserForm.role}
                onChange={(event) => setPlatformUserForm((prev) => ({ ...prev, role: event.target.value }))}
                className="w-full px-3 py-2 rounded-md border text-sm"
              >
                <option value="superuser">superuser</option>
                <option value="support">support</option>
                <option value="billing">billing</option>
              </select>
            </div>
            {!selectedPlatformUser && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Şifre (opsiyonel)</label>
                <input
                  type="password"
                  value={platformUserForm.password}
                  onChange={(event) => setPlatformUserForm((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="Boş bırakırsan otomatik üretilir"
                  className="w-full px-3 py-2 rounded-md border text-sm"
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input
                type="checkbox"
                checked={platformUserForm.isActive}
                onChange={(event) => setPlatformUserForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              Aktif kullanıcı
            </label>
          </div>
          <button type="submit" disabled={isSaving} className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
            Kaydet
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={isUserPasswordModalOpen}
        title={selectedPlatformUser ? `${selectedPlatformUser.username} Şifre Değiştir` : 'Şifre Değiştir'}
        onClose={() => setIsUserPasswordModalOpen(false)}
      >
        <form onSubmit={handleResetPlatformUserPassword} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Yeni Şifre</label>
            <input
              type="password"
              value={newPlatformPassword}
              onChange={(event) => setNewPlatformPassword(event.target.value)}
              placeholder="Boş bırakırsan otomatik üretilir"
              className="w-full px-3 py-2 rounded-md border text-sm"
            />
          </div>
          <button type="submit" disabled={isSaving} className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
            Şifreyi Güncelle
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={passwordModalState.isOpen}
        title={passwordModalState.title || 'Yeni Şifre'}
        onClose={closePasswordModal}
      >
        <div className="space-y-3">
          {passwordModalState.username ? (
            <div className="text-sm text-gray-700">
              Kullanıcı: <span className="font-semibold">{passwordModalState.username}</span>
            </div>
          ) : null}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Şifre</label>
            <div className="px-3 py-2 rounded-md border bg-gray-50 text-sm font-mono break-all">
              {passwordModalState.password || '-'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyPasswordToClipboard}
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
            >
              Kopyala
            </button>
            <button
              type="button"
              onClick={closePasswordModal}
              className="px-4 py-2 rounded-md border text-sm"
            >
              Kapat
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlatformPanel;
